import React, { useEffect, useState } from 'react';
import Shield from '../../shared/components/Shield';
import TrafficButtons from '../../shared/components/TrafficButtons';
import IconBox from '../../shared/components/IconBox';
import { useNavigate } from 'react-router-dom';
import Indicator from '../../shared/components/Indicator';
import ScrollingText from '../../shared/components/ScrollingText';
import { useMobile } from '../../contexts/MobileContext';

import { ReactComponent as DragHandleIcon } from '../../assets/icons/dragHandle.svg';
import { ReactComponent as BotIcon } from '../../assets/icons/bot.svg';
import { useWidth, useViewport, Pill, fraudPillColors, gridTemplateOverview } from '../../shared/components/tableUtils';

export default function SiteListRow({ site, dragHandleProps }){
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { vw } = useViewport();
  const [botClicks, setBotClicks] = useState(0);
  const [clickPercentage, setClickPercentage] = useState(undefined);

  useEffect(() => {
    const b = Number(site?.botClicks||0);
    const t = b + Number(site?.humanClicks||0);
    setBotClicks(b);
    if (t) setClickPercentage((b/t)*100);
  }, [site]);

  const showDragHandle = !isMobile;
  const gridTemplate = gridTemplateOverview(isMobile, vw, showDragHandle);
  const rowStyle = { gridTemplateColumns:gridTemplate };
  const status = site?.status || null;
  const fraudLevel = site?.fraudLevel || null;
  const hasFraudLevel = Boolean(fraudLevel);
  const showFraudLevel = hasFraudLevel && status !== 'red';

  const [fraudRef, fraudW] = useWidth();
  const fraudLong = fraudW >= 180 && !(isMobile || vw <= 720);
  const showBotActivity = fraudW >= 180 && !(isMobile || vw <= 720);

  const [metricRef, metricW] = useWidth();
  const METRIC_SHOW_PCT_MIN = 180;
  const METRIC_SHOW_CELL_MIN = 120;
  const metricShowCell = (status !== 'red' && metricW >= METRIC_SHOW_CELL_MIN);
  const metricShowPct  = (metricW >= METRIC_SHOW_PCT_MIN && typeof clickPercentage === 'number');

  return (
    <div className="site-card site-card--row site-card--overview-row" style={rowStyle}>
      {showDragHandle && (
        <div className="site-card__cell site-card__cell--center">
          <div {...dragHandleProps}
               className="drag-handle">
            <DragHandleIcon className="icon xs drag-handle-icon" />
          </div>
        </div>
      )}
      <div className="site-card__cell site-card__cell--center">
        <IconBox website={site.website}/>
      </div>
      <div onClick={()=>navigate('/dashboard/site/'+site.website)}
           className="site-card__cell site-card__site site-card__site-link">
        <ScrollingText className="site-card__name" text={site.website}/>
      </div>
      <div className="site-card__cell site-card__cell--center">
        <Indicator className="site-card__indicator" site={site}/>
      </div>
      <div ref={fraudRef}
           className="site-card__cell site-card__cell--fraud">
        {status === null && !hasFraudLevel && <span>Unavailable</span>}
        {status === 'red' && (
          <Pill text={(isMobile||vw<=980)? 'Waiting' : (fraudLong ? 'Waiting for traffic' : 'Waiting')}
                bg={'var(--surface-contrast-low)'}
                fg={'var(--text-contrast-low)'} />
        )}
        {showFraudLevel && (
          <div className="site-card__fraud-content">
            {!(isMobile && vw <= 360) && fraudW >= 120 ? (
              <Shield className="icon base site-card__metric-icon"
                      fraudLevel={fraudLevel}/>
            ) : null}
            {(()=>{const {bg,fg}=fraudPillColors(fraudLevel);
                   const label=String(fraudLevel);
                   return <Pill text={label} bg={bg} fg={fg}/>;
            })()}
            {showBotActivity && <small className="site-card__muted-note">
                                  bot activity
                                </small>}
          </div>
        )}
      </div>
      <div ref={metricRef}
           className="site-card__cell site-card__cell--metric">
        {metricShowCell ? (
          <>
            <BotIcon className="icon base site-card__metric-icon--bot" />
            <span className="metric-value-primary site-card__nowrap">
              {botClicks.toLocaleString()}
            </span>
            {metricShowPct && (
              <>
                <span className="metric-value-separator site-card__nowrap">{' | '}</span>
                <span className="metric-value-secondary site-card__nowrap">{clickPercentage.toFixed(2)}%</span>
              </>
            )}
          </>
        ) : null}
      </div>
      <div className="site-card__status-cell">
        <TrafficButtons site={site}/>
      </div>
    </div>
  );
}
